import { PlanTypesEnum, SocialStarterPlanTypesEnum, EventsV1PlanTypesEnum } from './enums';
import { type Plan, type ApiPlanGroupResponse, type ParsedPlanGroupResponse } from '../../repositories';
import { convertObjectPropsToCamelCase } from '../../utils/convertToCamelCase';
import { EmailPlan, type PriceAsVOTier } from '../email-plan';
import type { PriceVO } from '../price';

export type PriceAsVO<T> = T extends { price: number }
  ? Omit<T, 'price'> & { price: PriceVO }
  : { [K in keyof T]: PriceAsVO<T[K]> };

type ParsedPlanGroupResponseWithEmailPlansVO = Omit<ParsedPlanGroupResponse, 'plans'> & {
  plans: EmailPlan[];
};

const freePlanTypes: Plan['type'][] = [
  PlanTypesEnum.Trial,
  PlanTypesEnum.Free,
  PlanTypesEnum.MobileFree,
  SocialStarterPlanTypesEnum.Free,
  SocialStarterPlanTypesEnum.Trial,
  EventsV1PlanTypesEnum.Free,
];

const socialStarterPlanTypes: Plan['type'][] = [
  SocialStarterPlanTypesEnum.Free,
  SocialStarterPlanTypesEnum.Trial,
  SocialStarterPlanTypesEnum.Bronze,
  SocialStarterPlanTypesEnum.Silver,
];

const isSocialStarterPlanType = (type: Plan['type']) => socialStarterPlanTypes.includes(type);

const isSocialStarterGroupByPlans = (plans: Plan[]): boolean =>
  plans.length > 0 && plans.every((plan) => isSocialStarterPlanType(plan.type));

const NO_RECOMMENDED_PLAN_ID = 0;

export class PlanGroup {
  private parsedApiResponse: ParsedPlanGroupResponseWithEmailPlansVO;

  constructor(apiResponse: ApiPlanGroupResponse) {
    function sortPlans(planGroupResponse: ParsedPlanGroupResponse): ParsedPlanGroupResponse {
      const { plans, planIdsBySeqNo } = planGroupResponse;

      const getSeqNo = (planId: number): number => {
        if (!planIdsBySeqNo) return planId;

        const entry = Object.entries(planIdsBySeqNo).find(([, id]) => id === planId);

        return entry !== undefined ? Number(entry[0]) : Number.MAX_SAFE_INTEGER;
      };

      const sortedPlans = [...plans].sort((a, b) => getSeqNo(a.id) - getSeqNo(b.id));

      return {
        ...planGroupResponse,
        plans: sortedPlans,
      };
    }

    function transformEmailPlansToVO(
      planGroupResponse: ParsedPlanGroupResponse,
      isSocialStarterGroup: boolean
    ): ParsedPlanGroupResponseWithEmailPlansVO {
      const emailPlans = planGroupResponse.plans.map((plan) => new EmailPlan(plan, isSocialStarterGroup));

      return {
        ...planGroupResponse,
        plans: emailPlans,
      };
    }

    const parsedResponse = convertObjectPropsToCamelCase(apiResponse);
    const isSocialStarterGroup = isSocialStarterGroupByPlans(parsedResponse.plans);
    const responseWithSortedPlans = sortPlans(parsedResponse);

    this.parsedApiResponse = transformEmailPlansToVO(responseWithSortedPlans, isSocialStarterGroup);
  }

  getCurrentPlanId(): number {
    // if current plan is canceled, currentPlanId is null
    // TOFIX
    return this.parsedApiResponse.currentPlanId ?? -1;
  }

  getIsCanceled(): boolean {
    return this.parsedApiResponse.currentPlanId === null;
  }

  getRecommendedPlanId(): number {
    return this.parsedApiResponse.recommendedPlanId;
  }

  getPaymentPlanId(): number {
    return this.parsedApiResponse.paymentPlanId;
  }

  getPlanGroupType(): string {
    return this.parsedApiResponse.planGroupType;
  }

  getAllPlans(): EmailPlan[] {
    return this.parsedApiResponse.plans;
  }

  getAllDisplayedPlans(): EmailPlan[] {
    const hiddenFreeTypes: Plan['type'][] = [PlanTypesEnum.Trial, PlanTypesEnum.Free, EventsV1PlanTypesEnum.Free];

    return this.getAllPlans().filter((plan) => !hiddenFreeTypes.includes(plan.getType()) && !plan.isHidden());
  }

  getDisplayedPlanTypes(): Plan['type'][] {
    return this.getAllDisplayedPlans().map((plan) => plan.getType());
  }

  getRecommendedPlanType(): Plan['type'] | null {
    return this.getRecommendedPlan()?.getType() ?? null;
  }

  getFirstTierForPlan(planType: string): PriceAsVOTier | undefined {
    return this.getPlanByType(planType)?.getFirstTier();
  }

  getPlanById(planId: number): EmailPlan | undefined {
    return this.parsedApiResponse.plans.find((plan) => plan.getId() === planId);
  }

  getPlanByType(type: string): EmailPlan | undefined {
    return this.parsedApiResponse.plans.find((plan) => plan.getType() === type);
  }

  getCurrentPlan(): EmailPlan | undefined {
    const currentPlanId = this.getCurrentPlanId();

    return this.parsedApiResponse.plans.find((plan) => plan.getId() === currentPlanId);
  }

  getRecommendedPlan(): EmailPlan | undefined {
    if (this.isCustomer() && !this.getIsCanceled() && !this.isCurrentPlanFree() && !this.isSocialStarter()) {
      return this.parsedApiResponse.plans.find((plan) => plan.getId() === this.getCurrentPlanId() + 1); // the next plan by price
    }

    const recommendedPlanId = this.getRecommendedPlanId();

    return this.parsedApiResponse.plans.find((plan) => plan.getId() === recommendedPlanId);
  }

  isCurrent(planId: number): boolean {
    return planId === this.getCurrentPlanId();
  }

  isRecommended(planId: number): boolean {
    const recommendedPlan = this.getRecommendedPlan();

    return recommendedPlan?.getId() === planId;
  }

  getPrice(planId: number, contactsAmount: number): PriceVO | undefined {
    const plan = this.getPlanById(planId);

    return plan?.getPriceForContacts(contactsAmount);
  }

  getPriceTiersForPlan(planId: number): PriceAsVOTier[] {
    const plan = this.getPlanById(planId);

    // we only want to list tier options below the treshold
    const CONTACTS_AMOUNT_TRESHOLD = 50000;

    return plan?.getPriceTiersBelowThreshold(CONTACTS_AMOUNT_TRESHOLD) || [];
  }

  getPriceTiers(): PriceAsVOTier[] {
    // for suspend users, we use the first displayed plan to get tiers for (because they have no recommended plan)
    const planToGetTiersFor = this.getRecommendedPlanId() || this.getAllDisplayedPlans()[0].getId();

    return this.getPriceTiersForPlan(planToGetTiersFor);
  }

  public getPurchasedContactTierMin(): number | null {
    const currentPlanTierMin = this.findCurrentTierMin(this.getCurrentPlan()?.getTiers());

    if (currentPlanTierMin !== null) {
      return currentPlanTierMin;
    }

    return this.findCurrentTierMin(this.getPriceTiers());
  }

  private findCurrentTierMin(tiers?: PriceAsVOTier[]): number | null {
    if (!tiers?.length) {
      return null;
    }

    const currentTier = tiers.find((tier) => tier.current === true);

    return currentTier?.tierMin ?? null;
  }

  isUnlimitedTier(tier: PriceAsVOTier): boolean {
    return tier.tierMax === -1;
  }

  public isCurrentPlanPaid() {
    return !this.currentPlanTypeIsOneOf(...freePlanTypes);
  }

  public isCurrentPlanFree() {
    return this.currentPlanTypeIsOneOf(...freePlanTypes);
  }

  public isCurrentPlanLite() {
    return this.currentPlanTypeIsOneOf(PlanTypesEnum.Bronze);
  }

  public isCurrentPlanStandard() {
    return this.currentPlanTypeIsOneOf(PlanTypesEnum.Silver);
  }

  public isCurrentPlanPremium() {
    return this.currentPlanTypeIsOneOf(PlanTypesEnum.Gold);
  }

  public isTrialer() {
    return this.currentPlanTypeIsOneOf(PlanTypesEnum.Trial);
  }

  public isCurrentPlanEvents() {
    return this.currentPlanTypeIsOneOf(
      EventsV1PlanTypesEnum.Free,
      EventsV1PlanTypesEnum.Bronze,
      EventsV1PlanTypesEnum.Silver,
      EventsV1PlanTypesEnum.Gold
    );
  }

  private currentPlanTypeIsOneOf(...planTypes: Plan['type'][]) {
    const plan = this.getCurrentPlan();

    if (!plan) {
      return false;
    }

    return planTypes.includes(plan.getType());
  }

  public isCustomer() {
    const hasNoRecommendedPlan = this.parsedApiResponse.recommendedPlanId === NO_RECOMMENDED_PLAN_ID;
    // suspend users have no recommended plan
    const isSuspended = this.getIsCanceled() && hasNoRecommendedPlan;

    const isNotTrialerAndNotCanceled = !this.isTrialer() && !this.getIsCanceled();

    // we want to return true for suspend users and non-suspended users who are not trialers and not canceled
    return isSuspended || isNotTrialerAndNotCanceled;
  }

  public is3PLGroup() {
    const threePlansPlanTypes = [
      PlanTypesEnum.Trial,
      PlanTypesEnum.Free,
      PlanTypesEnum.MobileFree,
      PlanTypesEnum.Bronze,
      PlanTypesEnum.Silver,
      PlanTypesEnum.Gold,
      EventsV1PlanTypesEnum.Free,
      EventsV1PlanTypesEnum.Bronze,
      EventsV1PlanTypesEnum.Silver,
      EventsV1PlanTypesEnum.Gold,
    ];

    return this.getAllPlans().every((plan) =>
      threePlansPlanTypes.includes(plan.getType() as PlanTypesEnum | EventsV1PlanTypesEnum)
    );
  }

  public is3PLTrialer() {
    return this.is3PLGroup() && this.isTrialer();
  }

  public is3PLCustomer() {
    return this.is3PLGroup() && this.isCustomer();
  }

  public isSocialStarter() {
    return this.getAllPlans().every((plan) => socialStarterPlanTypes.includes(plan.getType()));
  }

  public getSequenceNumberByPlanId(planId: number): number | undefined {
    const { planIdsBySeqNo } = this.parsedApiResponse;

    if (!planIdsBySeqNo) {
      return undefined;
    }

    const matchingEntry = Object.entries(planIdsBySeqNo).find(([, id]) => id === planId);

    return matchingEntry ? Number(matchingEntry[0]) : undefined;
  }
}
